import { useRef, useState, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { searchPlaces, getPlaceDetails } from "@/lib/api/admin";
import type { PlacePrediction, PlaceDetails } from "@/lib/api/types";
import { Spinner } from "@/components/Spinner";

type Props = {
  label?: string;
  id?: string;
  value: string;
  onChange: (value: string) => void;
  onSelect: (details: PlaceDetails) => void;
  error?: string;
  maxLength?: number;
};

function newToken(): string {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/** Google Places street address input — proxied via the backend's public places endpoints. */
export function AddressAutocomplete({
  label,
  id,
  value,
  onChange,
  onSelect,
  error,
  maxLength,
}: Props) {
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const sessionToken = useRef(newToken());
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const search = useCallback(async (input: string) => {
    setIsLoading(true);
    try {
      const results = await searchPlaces(input, sessionToken.current);
      setPredictions(results);
    } catch {
      setPredictions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    onChange(v);
    if (debounce.current) clearTimeout(debounce.current);
    if (v.length < 3) {
      setPredictions([]);
      return;
    }
    debounce.current = setTimeout(() => search(v), 300);
  };

  const handleSelect = async (prediction: PlacePrediction) => {
    setPredictions([]);
    try {
      const details = await getPlaceDetails(
        prediction.place_id,
        sessionToken.current,
        prediction.main_text
      );
      onChange(details.address_line1 ?? "");
      onSelect(details);
      sessionToken.current = newToken();
    } catch {
      // keep existing value on error
    }
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setPredictions([]);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={containerRef} className="space-y-1.5">
      {label && <Label htmlFor={id}>{label}</Label>}
      <div className="relative">
        <Input
          id={id}
          value={value}
          onChange={handleChange}
          maxLength={maxLength}
          aria-invalid={!!error}
          placeholder="123 Main St"
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Spinner className="size-4 border-primary" />
          </div>
        )}
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      {typeof maxLength === "number" && (
        <p
          className={`text-xs text-right ${
            value.length > maxLength ? "text-destructive" : "text-muted-foreground"
          }`}
        >
          {value.length}/{maxLength}
        </p>
      )}
      {predictions.length > 0 && (
        <div className="bg-white border border-border rounded-md shadow-md overflow-hidden z-10 relative">
          {predictions.map((p) => (
            <button
              key={p.place_id}
              type="button"
              onClick={() => handleSelect(p)}
              className="w-full flex items-start gap-2 px-3 py-2.5 hover:bg-muted text-left transition-colors text-sm"
            >
              <svg
                className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <div>
                <p className="font-medium text-foreground">{p.main_text}</p>
                <p className="text-xs text-muted-foreground">{p.secondary_text}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
